<template>
  <ChartCard :data-widget-id="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="source-categories-for-hires"
    />
    <ChartContent
      height="284px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBarChart"
      class="px-3 pt-2 pb-0"
    >
      <BarChart :id="widgetId" :data="chartData" :options="chartOptions" :plugins="plugins" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { calculatePercent } from '@/components/insights/helpers';
import {
  dataLabelsConfig,
  flippedAxisTooltipPluginConfig,
  xScaleWithPercentFormatConfig,
  basicYScaleConfig,
} from '@/api/helpers/insights/commonChartConfigs';
import { startCase } from 'lodash-es';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'What outbound sources do our hires come from?';

const plugins = [ChartDataLabels];

const widgetId = 'outbound-top-sources-by-hire';

const {
  sourceOther,
  red700,
  basicRed,
  red300,
  red200,
  red100,
} = useColors();

const noWidgetData = computed(() => props.data.length === 0
  || props.data.every((item) => item.hired === 0));

const topSources = computed(() => {
  const sorted = [...props.data].sort((a, b) => b.hired - a.hired);
  return sorted.slice(0, 5).concat({
    ats_source_label: 'Other Outbound Sources',
    hired: sorted.slice(5).reduce((acc, item) => acc + item.hired, 0),
  });
});

const chartData = computed(() => {
  const totalHired = topSources.value.reduce((acc, item) => acc + item.hired, 0);
  const labels = topSources.value.map((item) => startCase(item.ats_source_label));
  const colors = [
    red700.value,
    basicRed.value,
    red300.value,
    red200.value,
    red100.value,
    sourceOther.value,
  ];

  return {
    labels,
    datasets: [
      {
        label: '',
        backgroundColor: colors,
        borderColor: colors,
        fill: false,
        maxBarThickness: 24,
        barThickness: 'flex',
        data: topSources.value.map((item) => calculatePercent(item.hired, totalHired)),
      },
    ],
  };
});

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: flippedAxisTooltipPluginConfig(chartData, widgetId),
    datalabels: dataLabelsConfig,
  },
  scales: {
    x: xScaleWithPercentFormatConfig,
    y: basicYScaleConfig,
  },
}));
</script>
